import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { activeItem, type NavSection } from "@/lib/nav";

/**
 * The second navigation column: the pages inside the section you are in.
 * Collapses to an icon rail, and the choice persists — the Compliance
 * section has seven pages, so the width is worth reclaiming on a laptop.
 *
 * Hidden below `md`, where a 200px column would eat the content.
 */
const PANEL_W = 200;
const RAIL_W = 48;
const KEY = "vp-section-nav-collapsed";

export function SectionNav({ section }: { section: NavSection }) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(KEY) === "1");
  const current = activeItem(section, pathname);

  useEffect(() => {
    localStorage.setItem(KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  return (
    <aside
      className="hidden shrink-0 flex-col border-r border-border bg-bg-secondary transition-[width] duration-200 md:flex"
      style={{ width: collapsed ? RAIL_W : PANEL_W }}
    >
      <div
        className={`flex h-16 shrink-0 items-center border-b border-border ${
          collapsed ? "justify-center px-2" : "gap-2.5 px-4"
        }`}
      >
        {!collapsed && (
          <>
            <i className={`fa-solid ${section.icon} text-[13px] text-accent`} />
            <span className="flex-1 truncate font-display text-sm font-semibold text-text-heading">
              {section.label}
            </span>
          </>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? `Expand ${section.label}` : `Collapse ${section.label}`}
          aria-label={collapsed ? `Expand ${section.label}` : `Collapse ${section.label}`}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-text-muted transition-colors hover:bg-bg-card-hover hover:text-text-heading"
        >
          <i
            className={`fa-solid ${collapsed ? "fa-angles-right" : "fa-angles-left"} text-[11px]`}
          />
        </button>
      </div>

      <nav className={`flex-1 overflow-y-auto py-3 ${collapsed ? "px-2" : "px-2.5"}`}>
        {section.items.map((item) => {
          const isActive = current?.to === item.to;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              className={[
                "mb-0.5 flex items-center rounded-md py-2 text-sm font-medium transition-colors",
                collapsed ? "justify-center px-0" : "gap-3 px-3",
                isActive
                  ? "bg-accent-muted text-text-heading"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-heading",
              ].join(" ")}
            >
              <i
                className={`fa-solid ${item.icon} w-4 shrink-0 text-center text-[13px] ${
                  isActive ? "text-accent" : ""
                }`}
              />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
